<template>
  <v-dialog v-model="openDialog"
            lazy
            persistent
            @input="v => v"
            :full-width="!isMobile"
            max-width="600">
    <v-card class="pa-4">
      <v-toolbar card>
        <v-toolbar-title flat>Repeat a Customer Load</v-toolbar-title>
      </v-toolbar>

      <v-list>

        <v-list-tile avatar>
          <v-list-tile-action>
            <v-icon color="green">verified_user</v-icon>
          </v-list-tile-action>

          <h3>{{ sponsor }}</h3>
          <v-spacer/>
        </v-list-tile>

        <v-list-tile>
          <v-list-tile-action>
            <v-chip color="accent" text-color="white" small>
              <v-icon>arrow_upward</v-icon>
            </v-chip>
          </v-list-tile-action>

          <v-list-tile-content class="pl-2">
            <v-text-field disabled
                          :value="origin"
                          label="From (Origin Address)">
            </v-text-field>
          </v-list-tile-content>
        </v-list-tile>

        <v-list-tile>
          <v-list-tile-action>
            <v-chip color="accent" small text-color="white">
              <v-icon>play_for_work</v-icon>
            </v-chip>
          </v-list-tile-action>

          <v-list-tile-content class="pl-2">
            <v-text-field disabled
                          :value="destination"
                          label="To (Destination Address)">
            </v-text-field>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>

      <v-container fluid>
        <h2>New Load Details:</h2>
        <v-layout row>
          <v-icon class="pl-2 pr-2 mt-2" color="primary">event</v-icon>
          <date-picker
            input-id="copyPickupTimePicker"
            placeholder="Next Pickup Date"
            v-model="copyPickupTime">
          </date-picker>
        </v-layout>
        <v-layout row>
          <v-icon class="pl-2 pr-2" color="primary">local_shipping</v-icon>
          <v-truck-counter-field v-model="copyLoadCount"
                                 rightLabel="trucks needed"
                                 class="mt-2"
                                 :min="1">
            <v-icon slot="increment">add</v-icon>
            <v-icon slot="decrement">remove</v-icon>
          </v-truck-counter-field>
        </v-layout>
        <v-layout row>
          <v-textarea v-model="copyDispatcherInstructions"
                      :rows="2"
                      class="mt-2"
                      label="Dispatcher instructions">
          </v-textarea>
        </v-layout>
        <v-layout>
          <v-textarea v-model="copyDriverInstructions"
                      :rows="2"
                      class="mt-2"
                      label="Driver instructions">
          </v-textarea>
        </v-layout>
      </v-container>

      <v-card-actions>
        <v-spacer />
        <v-btn color="accent"
               :loading="loadingBtn"
               :disabled="!validPickupDate"
               @click="createLoadFromTemplate()">
          Confirm
        </v-btn>
        <v-btn color="accent" flat
               @click="$emit('cancel')">
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>

  </v-dialog>

</template>

<script>
import moment from 'moment';
import API from '@/api';

import * as Timestamps from '@/types/timestamp';
import DatePicker from '@/components/DatePicker';
import VTruckCounterField from '@/components/VTruckCounterField';
import loadGroupMixin from '@/mixins/loadGroupMixin';

export default {
  name: 'VRepeatLoadGroupDialog',
  components: {
    VTruckCounterField,
    DatePicker,
  },
  mixins: [
    loadGroupMixin,
  ],
  props: {
    company: {
      type: Object,
      required: true,
    },
    loadGroupId: {
      type: String,
      required: true,
    },
    sponsor: {
      type: String,
      required: true,
    },
    origin: {
      type: String,
      required: true,
    },
    destination: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    count: {
      type: Number,
      required: true,
    },
    loadGroup: {
      type: Object,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
    value: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    copyPickupTime: null,
    copyLoadCount: null,
    copyDispatcherInstructions: null,
    copyDriverInstructions: null,
    openDialog: this.value,
    loadingBtn: false,
  }),
  watch: {
    value() {
      this.openDialog = this.value;
      if (!this.value) {
        this.resetForm();
      } else {
        this.initForm();
        this.copyPickupTime = moment().minute(0).second(0).format('YYYY-MM-DDTHH:mm:ss');
      }
    },
    openDialog() {
      this.$emit('input', this.openDialog);
    },
  },
  methods: {
    async createLoadFromTemplate() {
      this.loadingBtn = true;
      this.$emit('update:loading', true);

      const pickupTime = Timestamps.localizedTimestamp(this.copyPickupTime);
      const loadId = this.loadGroupId;
      const loadCount = this.copyLoadCount;
      const driverInstructions = this.copyDriverInstructions;
      const dispatchInstructions = this.copyDispatcherInstructions;

      const body = {
        pickupTime,
        loadCount,
        dispatchInstructions,
        driverInstructions,
      };

      try {
        const response = await API.planner.repeatLoad(loadId, body);
        this.$store.dispatch('plannedLoads/getPlannedLoads', this.company.id);
        this.$store.dispatch('repeatingLoadTemplates/getRepeatingLoadTemplates', this.company.id);
        this.$emit('newLoad', response.data);
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: 'An error occurred, please try again later',
        });
      } finally {
        this.loadingBtn = false;
        this.$emit('update:loading', false);
      }
    },
    resetForm() {
      this.copyLoadCount = null;
      this.copyPickupTime = null;
      this.copyDispatcherInstructions = null;
      this.copyDriverInstructions = null;
    },
    initForm() {
      this.copyLoadCount = this.count;
      this.copyDispatcherInstructions = this.loadGroupDispatchInstructions(this.loadGroup);
      this.copyDriverInstructions = this.loadGroupDriverInstructions(this.loadGroup);
    },
  },
  computed: {
    validPickupDate() {
      return !!this.copyPickupTime;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/RepeatLoadGroupDialog.vue